import {CustomDateRangeModel} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {TransactionSourceConfig} from "../../leasing/leasing.model";
import {BatchType} from "../run-tenant-payouts/model/run-payouts.model";
import {
  Currency,
  Date,
  DisplayFunction,
  DisplayOrder,
  ExportFunction,
  Hidden,
  String,
  TableDisplayedDefault,
  TableIdentifier,
  Number, SortDefault
} from '@ui-kit/webkit/components/crud-item/crud-item-decorator';
import {displayStatus, displayString, displayUuid} from '@ui-kit/framework/util/static/string-format-util';
import {displayDateWithTime, displayShortDate} from '@ui-kit/framework/util/static/date-format-utils';

export class DailyTransactionsSummary {
  leaseUuid!: string;
  retailerId!: number;
  retailerCompanyName!: string;
  transactionDate!: string;
  netBalancePayableRetailerInclRounded!: number;
  feesTotalIncl!: number;
  turnoverRentalFeeIncl!: number;
  transactionFeeIncl!: number;
  saleAmountIncl!: number;
  transactionCount!: number;
  totalFloorSpace!: number;
  paymentsCollectedIncl!: number;
  paymentsExternalIncl!: number;
}

export interface TransactionsSummary {
  uuid: string,
  leaseUuid: string,
  retailerId: number,
  retailerCompanyName: string,
  productName: string,
  paymentMethods: string,
  quantity: number,
  districtUuid: string,
  paymentBatchUuid: string,
  transactionDate: string,
  saleStatus: string,
  processingStatus: string,
  pointOfSaleStatus: string,
  saleType: string,
  returnFor: string,
  returnUuids: string[],
  transactionSource: TransactionSourceConfig
  outletId: string,
  customerId: string,
  transactionInvoiceNumber: string,
  currency: string,
  saleAmount: number,
  saleAmountTax: number,
  saleAmountIncl: number,

  transactionFee: number,
  transactionFeeTax: number,
  transactionFeeIncl: number,

  turnoverRentalFee: number,
  turnoverRentalFeeTax: number,
  turnoverRentalFeeIncl: number,

  feesTotal: number,
  feesTotalTax: number,
  feesTotalIncl: number,

  paymentsCollectedIncl: number,
  paymentsCollectedExcl: number,
  paymentsExternalIncl: number,
  paymentsExternalExcl: number,

  netBalancePayableRetailer: number,
  netBalancePayableRetailerIncl: number,
  netBalancePayableRetailerInclRounded: number
}

export class CrudTransactionsSummary {
  @DisplayOrder(0)
  @Number("ID")
  uuid?: string;

  @String("Batch ID")
  @DisplayOrder(1)
  @TableDisplayedDefault
  @DisplayFunction(displayUuid)
  paymentBatchId?: string;

  @String("Invoice Number")
  @DisplayOrder(2)
  @TableDisplayedDefault
  transactionInvoiceNumber?: string;

  @String("Retailer")
  @DisplayOrder(3)
  @TableDisplayedDefault
  @DisplayFunction(displayString)
  retailerCompanyName?: string;

  @Date("Date", "yyyy-MM-dd")
  @DisplayOrder(4)
  @TableDisplayedDefault
  @DisplayFunction(displayShortDate)
  @ExportFunction(displayShortDate)
  @SortDefault("desc")
  transactionDate?: string | Date | undefined;

  @String("Product Name")
  @DisplayOrder(5)
  @TableDisplayedDefault
  @DisplayFunction(displayString)
  productName?: string;

  @String("Payment Methods")
  @DisplayOrder(6)
  @TableDisplayedDefault
  @DisplayFunction(displayString)
  paymentMethods?: string;

  @Number("Quantity")
  @DisplayOrder(7)
  @TableDisplayedDefault
  quantity?: number;

  @Currency("Total Sales", "R")
  @DisplayOrder(8)
  @TableDisplayedDefault
  saleAmountIncl?: number;

  @Currency("Transaction Fee", "R")
  @DisplayOrder(9)
  @TableDisplayedDefault
  transactionFeeIncl?: number;

  @Currency("Rental Fee", "R")
  @DisplayOrder(10)
  @TableDisplayedDefault
  turnoverRentalFeeIncl?: number;

  @Currency("Payable Balance", "R")
  @DisplayOrder(11)
  @TableDisplayedDefault
  netBalancePayableRetailerInclRounded?: number;

  @String("Point of Sale Status")
  @DisplayOrder(12)
  @TableDisplayedDefault
  pointOfSaleStatus?: string;

  @String("Sale Status")
  @DisplayOrder(13)
  @TableDisplayedDefault
  saleStatus?: string;

  @String("Payout Status")
  @DisplayOrder(14)
  @TableDisplayedDefault
  processingStatus?: string;

  constructor(obj: any = {}) {
    obj = obj || {};
    this.uuid = obj.uuid;
    this.paymentBatchId = obj.paymentBatchId;
    this.transactionInvoiceNumber = obj.transactionInvoiceNumber;
    this.retailerCompanyName = obj.retailerCompanyName;
    this.transactionDate = obj.transactionDate;
    this.productName = obj.productName;
    this.paymentMethods = obj.paymentMethods;
    this.quantity = obj.quantity;
    this.saleAmountIncl = obj.saleAmountIncl;
    this.transactionFeeIncl = obj.transactionFeeIncl;
    this.turnoverRentalFeeIncl = obj.turnoverRentalFeeIncl;
    this.netBalancePayableRetailerInclRounded = obj.netBalancePayableRetailerInclRounded;
    this.pointOfSaleStatus = obj.pointOfSaleStatus;
    this.saleStatus = obj.saleStatus;
    this.processingStatus = obj.processingStatus;
  }
}

export interface LeaseNotInPaymentBatch {
  retailerId: number;
  companyName: string;
  leaseUuid: string;
  leaseStartDate: Date;
  leaseEndDate: Date;
  leaseStatus: string;
  leaseType: string;
  leasePaymentConfigStatus: string;
  leaseBillingStatus: string;
}

export interface AddLeaseToBatchRequest {
  batchUuid: string;
  leaseUuids: string[];
}

export interface PaymentBatchMembership {
  active: boolean;
  uuid: string;
  name: string;
  lastRunDate: Date;
  leaseUuid: string;
  linkedRetailers: LinkedRetailer[];
  selected: boolean;
  configType: string;
  startDate: string;
  endDate: string;
  dateFilter: CustomDateRangeModel[];
}

export interface LinkedRetailer {
  retailerId: number;
  companyName: string;
  leaseUuid: string;
  paymentGroupUuid: string;
  leaseStartDate: Date;
  leaseEndDate: Date;
}

export interface PaymentBatchGroupRequest {
  paymentGroupUuid: string;
  groupType: string;
  startDate: string;
  endDate: string;
}

export interface PaymentBatch {
  uuid: string,
  districtUuid: string,
  processedDate: string,
  paymentDate: string,
  paymentGroupUuid: string,
  paymentGroupName: string,
  processStatus: BatchStatus,
  batchTotalAmount: number,
  districtPayoutAmount: number,
  retailerPayoutAmount: number,
  currency: string,
  transactionsCountTotal: number,
  transactionsCountFail: number,
  transactionPaymentReference: string,
  payoutType: BatchType,
  batchTransactions: any[]
}

export class RetailerBatchSummary {
  @Hidden
  leaseUuid?: string;

  @String('Payout ID')
  @DisplayOrder(1)
  @TableIdentifier
  @DisplayFunction(displayUuid)
  paymentBatchUuid?: string;


  @Hidden
  districtUuid?: string;

  @Date('Date', 'yyyy-MM-dd HH:mm:ss.SSS')
  @DisplayOrder(3)
  @TableDisplayedDefault
  @DisplayFunction(displayDateWithTime)
  @ExportFunction(displayDateWithTime)
  @SortDefault("desc")
  processedDate?: string;

  @Date('Includes Transaction From', 'yyyy-MM-dd HH:mm:ss.SSS')
  @DisplayOrder(4)
  @TableDisplayedDefault
  @DisplayFunction(displayDateWithTime)
  @ExportFunction(displayDateWithTime)
  earliestTransactionDate?: string;

  @Date('Includes Transaction To', 'yyyy-MM-dd HH:mm:ss.SSS')
  @DisplayOrder(5)
  @TableDisplayedDefault
  @DisplayFunction(displayDateWithTime)
  @ExportFunction(displayDateWithTime)
  latestTransactionDate?: string;


  @Hidden
  paymentDate?: string;

  @String("Pay-out Status")
  @DisplayOrder(12)
  @TableDisplayedDefault
  @DisplayFunction(displayStatus)
  processStatus?: string;

  @Hidden
  retailerId?: number;

  @String("Company Name")
  @DisplayOrder(2)
  @TableDisplayedDefault
  @DisplayFunction(displayString)
  retailerCompanyName?: string;

  @Hidden
  rollupNetBalancePayable?: number;

  @Currency("Balance Payable", "R")
  @DisplayOrder(11)
  @TableDisplayedDefault
  rollupNetBalancePayableRounded?: number;

  @Hidden
  transactionCount?: number;


  @Currency("Total Sales", "R")
  @DisplayOrder(6)
  @TableDisplayedDefault
  saleAmountInclTotal?: number;

  @Currency("Internal Payments", "R")
  @DisplayOrder(8)
  @TableDisplayedDefault
  paymentsCollectedInclTotal?: number;

  @Currency("External Payments", "R")
  @DisplayOrder(7)
  @TableDisplayedDefault
  paymentsExternalInclTotal?: number;

  @Currency("Transaction Fee", "R")
  @DisplayOrder(9)
  @TableDisplayedDefault
  transactionFeeInclTotal?: number;

  @Currency("Rental Fee", "R")
  @DisplayOrder(10)
  @TableDisplayedDefault
  turnoverRentalFeeInclTotal?: number;

  constructor(obj: any = {}) {
    obj = obj || {};
    this.leaseUuid = obj.leaseUuid;
    this.paymentBatchUuid = obj.paymentBatchUuid;
    this.districtUuid = obj.districtUuid;
    this.processedDate = obj.processedDate;
    this.paymentDate = obj.paymentDate;
    this.earliestTransactionDate = obj.earliestTransactionDate;
    this.latestTransactionDate = obj.latestTransactionDate;
    this.processStatus = obj.processStatus;
    this.retailerId = obj.retailerId;
    this.retailerCompanyName = obj.retailerCompanyName;
    this.rollupNetBalancePayable = obj.rollupNetBalancePayable;
    this.rollupNetBalancePayableRounded = obj.rollupNetBalancePayableRounded;
    this.transactionCount = obj.transactionCount;
    this.saleAmountInclTotal = obj.saleAmountInclTotal;
    this.paymentsCollectedInclTotal = obj.paymentsCollectedInclTotal;
    this.paymentsExternalInclTotal = obj.paymentsExternalInclTotal;
    this.transactionFeeInclTotal = obj.transactionFeeInclTotal;
    this.turnoverRentalFeeInclTotal = obj.turnoverRentalFeeInclTotal;

  }
}

export interface GenericChart {
  chartSeriesName: string,
  chartData: GenericChartData[],
  chartInfo: GenericChartInfo
}

export interface GenericChartData {
  data: number,
  dateLabel: string;
  intLabel: number;
}

export interface GenericChartInfo {
  saleAmountIncl: number,
  averageBasketSize: number,
  averageTradingDensity: number,
  transactionCount: number,
  startDate: string,
  endDate: string
}

export enum SaleStatus {
  SAVED = 'SAVED',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  VOID = 'VOID',
  UNKNOWN = 'UNKNOWN'
}

export enum TransactionStatus {
  ON_HOLD = 'ON_HOLD',
  NEW = 'NEW',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  ERROR_PAYMENT_CONFIG = 'ERROR_PAYMENT_CONFIG',
  ERROR_PRICING_PERIOD = 'ERROR_PRICING_PERIOD',
  STAGED_IN_PAYMENT_BATCH = 'STAGED_IN_PAYMENT_BATCH',
  STAGED_IN_PAYMENT_ERROR = 'STAGED_IN_PAYMENT_ERROR',
  SUBMITTED_FOR_PAYMENT = 'SUBMITTED_FOR_PAYMENT',
  PAID = 'PAID',
  ERROR_PAYMENT_FAILED = 'ERROR_PAYMENT_FAILED',
  PENDING_REFUND_RECOVERY = 'PENDING_REFUND_RECOVERY',
  STAGED_FOR_RECOVERY = 'STAGED_FOR_RECOVERY',
  REFUND_ERROR_NO_PARENT = 'REFUND_ERROR_NO_PARENT',
  SUBMITTED_FOR_RECOVERY = 'SUBMITTED_FOR_RECOVERY',
  SUBMITTED_FOR_RECOVERY_ERROR = 'SUBMITTED_FOR_RECOVERY_ERROR',
  RECOVERED = 'RECOVERED',
  RECOVERY_ERROR = 'RECOVERY_ERROR',
  ERROR_INSUFFICIENT_MONEY_COLLECTED = 'ERROR_INSUFFICIENT_MONEY_COLLECTED',
  VOID = 'VOID',
  ERROR_UNKNOWN_SALE_STATUS = 'ERROR_UNKNOWN_SALE_STATUS',
  ERROR_NO_LINKED_SUPPLIER_FOUND = 'ERROR_NO_LINKED_SUPPLIER_FOUND',
  ERROR_PAYMENT_METHOD_ALLOCATION_NEEDED = 'ERROR_PAYMENT_METHOD_ALLOCATION_NEEDED'
}

export enum POSSaleStatus {
  SAVED = 'SAVED',
  CLOSED = 'CLOSED',
  ONACCOUNT = 'ONACCOUNT',
  ONACCOUNT_CLOSED = 'ONACCOUNT_CLOSED',
  LAYBY = 'LAYBY',
  LAYBY_CLOSED = 'LAYBY_CLOSED',
  AWAITING_DISPATCH = 'AWAITING_DISPATCH',
  AWAITING_PICKUP = 'AWAITING_PICKUP',
  DISPATCHED_CLOSED = 'DISPATCHED_CLOSED',
  PICKED_UP_CLOSED = 'PICKED_UP_CLOSED',
  VOIDED = 'VOIDED',
  UNKNOWN = 'UNKNOWN'
}

export enum BatchStatus {
  UNSUBMITTED = 'UNSUBMITTED',
  SUBMITTED = 'SUBMITTED',
  SUBMIT_FAILED = 'SUBMIT_FAILED',
  PROCESSED = 'PROCESSED',
  PROCESS_ERROR = 'PROCESS_ERROR'
}
